import {
  PeriodTransaction,
  DashboardProduct,
  DashboardOffer,
  PerformanceThresholds,
  DEFAULT_THRESHOLDS,
  LineChartData,
} from '../types';
import {
  ProductPerformance,
  ProductTimeSeriesData,
  OfferPerformance,
} from './productTypes';
import { getTimeLabels, getStoreColor, getGroupKey } from '../FinancialDashboardController';

/**
 * Aggregates product sales data from PeriodTransaction arrays
 */
export function aggregateProductData(
  transactions: PeriodTransaction[],
  products: DashboardProduct[],
): ProductPerformance[] {
  if (!transactions.length || !products.length) return [];

  // Create a map of product code to product title
  const productTitleMap = new Map<string, string>();
  products.forEach(p => {
    productTitleMap.set(p.Code, p.Title);
  });

  // Aggregate data by product code
  const productDataMap = new Map<string, {
    totalUnits: number;
    totalRevenue: number;
    totalRevenueExVAT: number;
    totalVATAmount: number;
    vat: number;
    periodData: Map<string, { units: number; revenue: number }>;
  }>();

  transactions.forEach(transaction => {
    // Skip if no products in period
    if (!transaction.ProductsInPeriod || transaction.ProductsInPeriod.length === 0) return;

    // Create period key for time series tracking
    let periodKey: string;
    if (transaction.Month != null) {
      periodKey = `${transaction.Year}-${String(transaction.Month).padStart(2, '0')}`;
    } else if (transaction.Week != null) {
      periodKey = `${transaction.Year}-W${String(transaction.Week).padStart(2, '0')}`;
    } else {
      periodKey = `${transaction.Year}`;
    }

    transaction.ProductsInPeriod.forEach(productInPeriod => {
      const productCode = productInPeriod.ProductCode;

      // Skip null product codes
      if (!productCode) return;

      if (!productDataMap.has(productCode)) {
        productDataMap.set(productCode, {
          totalUnits: 0,
          totalRevenue: 0,
          totalRevenueExVAT: 0,
          totalVATAmount: 0,
          vat: productInPeriod.VAT || 0,
          periodData: new Map(),
        });
      }

      const productData = productDataMap.get(productCode)!;
      const vatRate = productInPeriod.VAT || 0;
      const revenueExVAT = productInPeriod.TotalAmountPaid / (1 + vatRate / 100);
      const vatAmount = productInPeriod.TotalAmountPaid - revenueExVAT;

      productData.totalUnits += productInPeriod.Units;
      productData.totalRevenue += productInPeriod.TotalAmountPaid;
      productData.totalRevenueExVAT += revenueExVAT;
      productData.totalVATAmount += vatAmount;

      // Track period-level data for trend calculation
      if (!productData.periodData.has(periodKey)) {
        productData.periodData.set(periodKey, { units: 0, revenue: 0 });
      }
      const periodData = productData.periodData.get(periodKey)!;
      periodData.units += productInPeriod.Units;
      periodData.revenue += productInPeriod.TotalAmountPaid;
    });
  });

  // Convert to ProductPerformance array
  const performances: ProductPerformance[] = [];

  productDataMap.forEach((data, productCode) => {
    const productTitle = productTitleMap.get(productCode) || productCode;
    const periodCount = data.periodData.size;

    // Calculate averages
    const avgUnitsPerPeriod = periodCount > 0 ? data.totalUnits / periodCount : 0;
    const avgRevenuePerPeriod = periodCount > 0 ? data.totalRevenue / periodCount : 0;

    // Determine trend direction using linear regression on last 6 periods
    const trendDirection = calculateTrendDirection(data.periodData);

    // Calculate performance score (0-100) based on revenue growth
    const performanceScore = calculatePerformanceScore(data.periodData, data.totalRevenue);

    performances.push({
      ProductCode: productCode,
      ProductTitle: productTitle,
      VAT: data.vat,
      TotalUnits: data.totalUnits,
      TotalRevenue: data.totalRevenue,
      TotalRevenueExVAT: data.totalRevenueExVAT,
      TotalVATAmount: data.totalVATAmount,
      AvgUnitsPerPeriod: avgUnitsPerPeriod,
      AvgRevenuePerPeriod: avgRevenuePerPeriod,
      TrendDirection: trendDirection,
      PerformanceScore: performanceScore,
    });
  });

  // Sort by total revenue descending
  performances.sort((a, b) => b.TotalRevenue - a.TotalRevenue);

  return performances;
}

/**
 * Calculate trend direction using simple linear regression
 */
function calculateTrendDirection(
  periodData: Map<string, { units: number; revenue: number }>
): 'up' | 'down' | 'stable' {
  const periods = Array.from(periodData.entries()).sort((a, b) => a[0].localeCompare(b[0]));

  // Need at least 2 periods for trend
  if (periods.length < 2) return 'stable';

  // Use last 6 periods for trend calculation
  const recentPeriods = periods.slice(-6);
  const n = recentPeriods.length;

  // Simple linear regression: y = mx + b
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;

  recentPeriods.forEach((period, index) => {
    const x = index;
    const y = period[1].revenue;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);

  // Determine trend based on slope
  const avgRevenue = sumY / n;
  const slopeThreshold = avgRevenue * 0.05; // 5% threshold

  if (slope > slopeThreshold) return 'up';
  if (slope < -slopeThreshold) return 'down';
  return 'stable';
}

/**
 * Calculate performance score (0-100) based on revenue growth and consistency
 */
function calculatePerformanceScore(
  periodData: Map<string, { units: number; revenue: number }>,
  totalRevenue: number
): number {
  if (periodData.size === 0) return 0;

  const periods = Array.from(periodData.entries()).sort((a, b) => a[0].localeCompare(b[0]));

  // Growth score (0-50 points)
  let growthScore = 0;
  if (periods.length >= 2) {
    const recentPeriods = periods.slice(-Math.min(6, periods.length));
    const firstPeriodRevenue = recentPeriods[0][1].revenue;
    const lastPeriodRevenue = recentPeriods[recentPeriods.length - 1][1].revenue;

    if (firstPeriodRevenue > 0) {
      const growthRate = ((lastPeriodRevenue - firstPeriodRevenue) / firstPeriodRevenue) * 100;
      // Cap growth score at 50
      growthScore = Math.min(50, Math.max(0, 25 + growthRate / 2));
    }
  }

  // Consistency score (0-50 points) - lower variance is better
  let consistencyScore = 0;
  if (periods.length >= 3) {
    const revenues = periods.map(p => p[1].revenue);
    const mean = revenues.reduce((sum, r) => sum + r, 0) / revenues.length;
    const variance = revenues.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / revenues.length;
    const stdDev = Math.sqrt(variance);
    const coefficientOfVariation = mean > 0 ? stdDev / mean : 1;

    // Lower CV means higher consistency
    consistencyScore = Math.max(0, 50 * (1 - Math.min(1, coefficientOfVariation)));
  }

  // Total revenue score contribution (bonus for high revenue)
  const revenueBonus = totalRevenue > 1000 ? 10 : totalRevenue > 500 ? 5 : 0;

  return Math.min(100, growthScore + consistencyScore + revenueBonus);
}

/**
 * Builds time series data for each product suitable for charting
 */
export function buildProductTimeSeries(
  transactions: PeriodTransaction[],
  products: DashboardProduct[],
  monthNames: string[],
  period?: 'month'
): ProductTimeSeriesData[] {
  if (!transactions.length || !products.length) return [];

  // Create a map of product code to product title
  const productTitleMap = new Map<string, string>();
  products.forEach(p => {
    productTitleMap.set(p.Code, p.Title);
  });

  // Get all time labels for the period
  const timeLabels = getTimeLabels(transactions, monthNames, 'month');

  // Build time series for each product
  const productTimeSeriesMap = new Map<string, Map<string, { units: number; revenue: number }>>();

  transactions.forEach(transaction => {
    if (!transaction.ProductsInPeriod || transaction.ProductsInPeriod.length === 0) return;

    // Create period key using the same grouping logic as chart labels
    const periodKey = getGroupKey(transaction, 'month');

    transaction.ProductsInPeriod.forEach(productInPeriod => {
      const productCode = productInPeriod.ProductCode;
      if (!productCode) return;

      if (!productTimeSeriesMap.has(productCode)) {
        productTimeSeriesMap.set(productCode, new Map());
      }

      const productData = productTimeSeriesMap.get(productCode)!;
      if (!productData.has(periodKey)) {
        productData.set(periodKey, { units: 0, revenue: 0 });
      }

      const periodData = productData.get(periodKey)!;
      periodData.units += productInPeriod.Units;
      periodData.revenue += productInPeriod.TotalAmountPaid;
    });
  });

  // Convert to ProductTimeSeriesData array
  const timeSeriesArray: ProductTimeSeriesData[] = [];

  productTimeSeriesMap.forEach((periodDataMap, productCode) => {
    const productTitle = productTitleMap.get(productCode) || productCode;

    // Build time series aligned to time labels
    const timeSeries = timeLabels.map(timeLabel => {
      const periodData = periodDataMap.get(timeLabel.key);

      return {
        PeriodKey: timeLabel.key,
        PeriodLabel: timeLabel.label,
        Units: periodData ? periodData.units : 0,
        Revenue: periodData ? periodData.revenue : 0,
        IsAnomaly: false,
      };
    });

    timeSeriesArray.push({
      ProductCode: productCode,
      ProductTitle: productTitle,
      TimeSeries: timeSeries,
    });
  });

  return timeSeriesArray;
}

/**
 * Analyzes offer performance using aggregated OffersInPeriod from monthly transactions.
 * Revenue is estimated proportionally: (offerUnits / periodTransactionCount) * periodTotalAmountPaid.
 */
export function calculateOfferPerformance(
  monthlyTransactions: PeriodTransaction[],
  offers: DashboardOffer[],
  _products: DashboardProduct[],
  thresholds: PerformanceThresholds = DEFAULT_THRESHOLDS
): OfferPerformance[] {
  if (!monthlyTransactions.length) return [];

  // Build offer metadata map from provided offers list
  const offerMap = new Map<string, DashboardOffer>();
  offers.forEach(offer => {
    offerMap.set(offer.OfferCode, offer);
  });

  // Accumulate units and estimated revenue per offer across all periods
  const offerStats = new Map<string, { totalUnits: number; estimatedRevenue: number }>();

  monthlyTransactions.forEach(period => {
    if (!period.OffersInPeriod || period.OffersInPeriod.length === 0) return;

    const safeTransactionCount = Math.max(1, period.TransactionCount);

    period.OffersInPeriod.forEach(offerInPeriod => {
      const { OfferCode, Units } = offerInPeriod;
      if (!Units) return;

      // Ensure metadata entry exists (create synthetic if not in the offers list)
      if (!offerMap.has(OfferCode)) {
        offerMap.set(OfferCode, {
          OfferCode,
          OfferTitle: `Offer ${OfferCode}`,
          DiscountPercentage: 0,
          FixedPrice: 0,
        });
      }

      if (!offerStats.has(OfferCode)) {
        offerStats.set(OfferCode, { totalUnits: 0, estimatedRevenue: 0 });
      }

      const stats = offerStats.get(OfferCode)!;
      stats.totalUnits += Units;
      stats.estimatedRevenue += (Units / safeTransactionCount) * period.TotalAmountPaid;
    });
  });

  const offerPerformances: OfferPerformance[] = [];

  offerStats.forEach((stats, offerCode) => {
    if (stats.totalUnits === 0) return;

    const offer = offerMap.get(offerCode)!;
    const avgRevenuePerTransaction = stats.estimatedRevenue / stats.totalUnits;

    let performance: 'excellent' | 'good' | 'average' | 'poor';
    if (
      avgRevenuePerTransaction > thresholds.excellent.minAvgRevenue &&
      stats.totalUnits > thresholds.excellent.minProductsSold
    ) {
      performance = 'excellent';
    } else if (
      avgRevenuePerTransaction > thresholds.good.minAvgRevenue ||
      stats.totalUnits > thresholds.good.minProductsSold
    ) {
      performance = 'good';
    } else if (
      avgRevenuePerTransaction > thresholds.average.minAvgRevenue ||
      stats.totalUnits > thresholds.average.minProductsSold
    ) {
      performance = 'average';
    } else {
      performance = 'poor';
    }

    offerPerformances.push({
      OfferCode: offer.OfferCode,
      OfferTitle: offer.OfferTitle,
      DiscountPercentage: offer.DiscountPercentage,
      FixedPrice: offer.FixedPrice,
      TotalRevenue: stats.estimatedRevenue,
      ProductsSold: stats.totalUnits,
      Performance: performance,
      AvgRevenuePerTransaction: avgRevenuePerTransaction,
    });
  });

  // Sort by performance rating, then by total revenue
  const performanceOrder = { excellent: 4, good: 3, average: 2, poor: 1 };
  offerPerformances.sort((a, b) => {
    const perfDiff = performanceOrder[b.Performance] - performanceOrder[a.Performance];
    if (perfDiff !== 0) return perfDiff;
    return b.TotalRevenue - a.TotalRevenue;
  });

  return offerPerformances;
}

/**
 * Prepares multi-product chart data with anomaly markers
 */
export function prepareProductComparisonData(
  timeSeriesArray: ProductTimeSeriesData[],
  selectedProductCodes: string[],
  viewMode: 'units' | 'revenue'
): LineChartData {
  if (!timeSeriesArray.length || !selectedProductCodes.length) {
    return { labels: [], datasets: [] };
  }

  // Filter time series to only selected products
  const selectedTimeSeries = timeSeriesArray.filter(ts =>
    selectedProductCodes.includes(ts.ProductCode)
  );

  if (!selectedTimeSeries.length) {
    return { labels: [], datasets: [] };
  }

  // Get time labels from first product (all products should have the same labels)
  const labels = selectedTimeSeries[0].TimeSeries.map(ts => ts.PeriodLabel);

  // Create datasets for each product
  const datasets = selectedTimeSeries.map((productTimeSeries, index) => {
    const { ProductTitle, TimeSeries } = productTimeSeries;

    // Get color for this product
    const color = getStoreColor(index);

    // Prepare data based on view mode
    const data = TimeSeries.map(ts => viewMode === 'revenue' ? ts.Revenue : ts.Units);


    return {
      label: ProductTitle,
      data,
      borderColor: color.border,
      backgroundColor: color.bg,
      borderWidth: 2,
      tension: 0.4
    };
  });

  return { labels, datasets };
}
